<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MetaTemplatesAPI from 'dashboard/api/inbox/metaTemplates';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  channel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();

const query = ref('');
const templates = ref([]);
const isLoading = ref(false);

const filteredTemplates = computed(() => {
  return templates.value.filter(template => {
    if (!template.approved_for_sending) return false;
    const search = query.value.toLowerCase();
    const buttonTitles = (
      template.additional_attributes?.components?.buttons || []
    )
      .map(button => button.title)
      .join(' ')
      .toLowerCase();
    return (
      template.name.toLowerCase().includes(search) ||
      template.body.toLowerCase().includes(search) ||
      buttonTitles.includes(search)
    );
  });
});

const loadTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await MetaTemplatesAPI.get(props.inboxId, {
      channel: props.channel,
    });
    templates.value = data.payload || [];
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.inboxId, props.channel],
  () => {
    loadTemplates();
  }
);

onMounted(loadTemplates);
</script>

<template>
  <div class="w-full">
    <div class="flex gap-2 mb-2.5">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus-within:outline-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-12" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('CONTENT_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <button
        class="flex justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 hover:bg-n-alpha-2 disabled:opacity-50"
        :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        @click="loadTemplates"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isLoading }"
        />
      </button>
    </div>

    <div
      class="bg-n-background outline-n-container outline outline-1 rounded-lg max-h-[18.75rem] overflow-y-auto p-2.5"
    >
      <div v-if="isLoading" class="py-8 text-center text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.LOADING') }}
      </div>

      <template v-else>
        <div v-for="(template, index) in filteredTemplates" :key="template.id">
          <button
            class="block p-2.5 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-2"
            @click="
              emit('onSelect', {
                ...template,
                friendly_name: template.name,
                template_type: 'template',
              })
            "
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ template.name }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11 truncate">
                  {{ template.channel }}
                </p>
              </div>
              <span
                class="inline-flex items-center px-2 py-1 text-[11px] leading-none rounded-lg bg-n-alpha-black2 text-n-slate-11"
              >
                {{ template.language }}
              </span>
            </div>
            <p class="mt-3 text-sm text-n-slate-12 whitespace-pre-wrap">
              {{ template.body }}
            </p>
            <div
              v-if="template.additional_attributes?.components?.buttons?.length"
              class="mt-3"
            >
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CONTENT_TEMPLATES.PICKER.BUTTONS') }}
              </p>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  v-for="button in template.additional_attributes.components
                    .buttons"
                  :key="`${template.id}-${button.title}`"
                  class="inline-flex items-center rounded-full bg-n-alpha-black2 px-2.5 py-1 text-[11px] text-n-slate-11"
                >
                  {{ button.title }}
                </span>
              </div>
            </div>
          </button>
          <hr
            v-if="index !== filteredTemplates.length - 1"
            class="border-b border-solid border-n-weak my-2.5 mx-auto max-w-[95%]"
          />
        </div>

        <div v-if="!filteredTemplates.length" class="py-8 text-center">
          <p class="text-n-slate-11">
            {{ t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.EMPTY') }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
